<template>
  <article class="mobile-vecura">
    <section class="mobile-vecura__hero">
      <img src="@/assets/vecura-logo.svg" alt="Vecura" />
      <p>{{ vecuraPage.eyebrow }}</p>
      <h1>{{ vecuraPage.headline }}</h1>
      <span>{{ vecuraPage.intro }}</span>
    </section>

    <section class="mobile-vecura__section mobile-vecura__role">
      <header>
        <span>Meine Rolle</span>
        <h2>Aufbau, Richtung und operative Verantwortung.</h2>
      </header>
      <p>{{ vecuraPage.role }}</p>
      <ul>
        <li v-for="point in vecuraPage.rolePoints" :key="point">
          {{ point }}
        </li>
      </ul>
    </section>

    <section class="mobile-vecura__section">
      <header>
        <span>Plattform</span>
        <h2>{{ vecuraPage.pitch }}</h2>
      </header>

      <div class="mobile-vecura__stats" aria-label="Vecura Kennzahlen">
        <article v-for="point in vecuraPage.proofPoints" :key="point.value">
          <strong>{{ point.value }}</strong>
          <span>{{ point.label }}</span>
        </article>
      </div>
    </section>

    <section class="mobile-vecura__section">
      <header>
        <span>Leistung</span>
        <h2>Daten, Suche, Quellen und Integration müssen zusammenarbeiten.</h2>
      </header>

      <div class="mobile-vecura__capabilities">
        <article
          v-for="(capability, index) in vecuraPage.capabilities"
          :key="capability.title"
          class="mobile-vecura__capability"
          :class="getCapabilityState(capability.title)"
        >
          <button
            class="mobile-vecura__capability-summary"
            type="button"
            :aria-expanded="isCapabilityOpen(capability.title)"
            @click="toggleCapability(capability.title)"
          >
            <MobileSymbolIcon :type="index === 1 ? 'expertise' : 'brain'" />
            <strong>{{ capability.title }}</strong>
          </button>
          <Transition name="mobile-vecura-disclosure">
            <p v-if="isCapabilityOpen(capability.title)">
              {{ capability.description }}
            </p>
          </Transition>
        </article>
      </div>
    </section>

    <section class="mobile-vecura__section mobile-vecura__case">
      <header>
        <span>Case Study</span>
        <h2>{{ vecuraPage.caseStudy.title }}</h2>
      </header>
      <p>{{ vecuraPage.caseStudy.summary }}</p>
      <ul>
        <li v-for="detail in vecuraPage.caseStudy.details" :key="detail">
          {{ detail }}
        </li>
      </ul>
    </section>

    <section class="mobile-vecura__section mobile-vecura__contact">
      <header>
        <span>Kontakt</span>
        <h2>Wenn Fachwissen schneller nutzbar werden soll.</h2>
      </header>

      <div class="mobile-vecura__icon-actions" aria-label="Vecura Kontakt">
        <a
          :href="vecuraWebsiteUrl"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Vecura Website öffnen"
        >
          <ContactChannelIcon type="vecura" />
        </a>
        <a
          :href="vecuraLinkedinUrl"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Vecura auf LinkedIn öffnen"
        >
          <ContactChannelIcon type="linkedin" />
        </a>
        <a :href="vecuraEmailHref" aria-label="E-Mail an Vecura schreiben">
          <ContactChannelIcon type="mail" />
        </a>
      </div>
    </section>
  </article>
</template>

<script setup lang="ts">
import { ref } from "vue";
import ContactChannelIcon from "@/components/icons/ContactChannelIcon.vue";
import MobileSymbolIcon from "@/components/icons/MobileSymbolIcon.vue";
import { vecuraPage } from "@/data/profile";

const vecuraWebsiteUrl = "https://vecura.io";
const vecuraEmailHref = "mailto:hello@vecura.io";
const vecuraLinkedinUrl = "https://www.linkedin.com/company/vecura-gmbh";
const openCapabilityTitles = ref<Set<string>>(
  new Set(vecuraPage.capabilities[0] ? [vecuraPage.capabilities[0].title] : [])
);

function isCapabilityOpen(title: string): boolean {
  return openCapabilityTitles.value.has(title);
}

function getCapabilityState(title: string): Record<string, boolean> {
  return {
    "mobile-vecura__capability--open": isCapabilityOpen(title),
  };
}

function toggleCapability(title: string): void {
  const nextOpenTitles = new Set(openCapabilityTitles.value);

  if (nextOpenTitles.has(title)) {
    nextOpenTitles.delete(title);
  } else {
    nextOpenTitles.add(title);
  }

  openCapabilityTitles.value = nextOpenTitles;
}
</script>

<style scoped>
.mobile-vecura {
  display: none;
  overflow-x: hidden;
  background: #0e151c;
  color: #f4f7fa;
}

.mobile-vecura__hero,
.mobile-vecura__section {
  padding: 30px 18px;
}

.mobile-vecura__hero {
  min-height: calc(100svh - var(--site-header-height) - 82px);
  background: linear-gradient(135deg, rgba(0, 181, 237, 0.18), transparent 42%),
    linear-gradient(180deg, #101a23 0%, #0e151c 80%);
}

.mobile-vecura__hero img {
  display: block;
  width: min(210px, 70vw);
  height: auto;
  margin: 8px 0 34px;
}

.mobile-vecura__hero > p,
.mobile-vecura__section header span {
  display: block;
  margin: 0;
  color: var(--color-vecura);
  font-size: 0.72rem;
  font-weight: 950;
  text-transform: uppercase;
}

.mobile-vecura h1,
.mobile-vecura h2 {
  margin: 0;
  color: #f4f7fa;
  text-wrap: balance;
  hyphens: auto;
  overflow-wrap: anywhere;
}

.mobile-vecura h1 {
  margin-top: 12px;
  font-size: clamp(2.1rem, 11vw, 3rem);
  line-height: 1.03;
}

.mobile-vecura h2 {
  margin-top: 10px;
  font-size: 1.55rem;
  line-height: 1.14;
}

.mobile-vecura__hero > span,
.mobile-vecura__section > p,
.mobile-vecura__case p {
  display: block;
  margin-top: 18px;
  color: rgba(229, 230, 231, 0.72);
  font-size: 0.98rem;
  line-height: 1.62;
}

.mobile-vecura__icon-actions {
  display: grid;
  grid-template-columns: repeat(3, 52px);
  gap: 8px;
  justify-content: center;
  margin-top: 22px;
}

.mobile-vecura__icon-actions a {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border: 1px solid rgba(229, 230, 231, 0.16);
  border-radius: 18px;
  background: rgba(229, 230, 231, 0.06);
  color: #f4f7fa;
  text-decoration: none;
  transition: background 180ms ease, border-color 180ms ease,
    transform 180ms ease;
}

.mobile-vecura__icon-actions a:active {
  transform: scale(0.96);
}

.mobile-vecura__icon-actions svg,
.mobile-vecura__icon-actions :deep(svg) {
  width: 20px;
  height: 20px;
}

.mobile-vecura__section {
  border-top: 1px solid rgba(229, 230, 231, 0.1);
  background: #0e151c;
}

.mobile-vecura__section:nth-of-type(even) {
  background: #111b24;
}

.mobile-vecura__role {
  position: relative;
}

.mobile-vecura__role::before {
  position: absolute;
  top: 0;
  right: 18px;
  left: 18px;
  height: 1px;
  background: linear-gradient(90deg, var(--color-vecura), transparent);
  content: "";
}

.mobile-vecura__role ul,
.mobile-vecura__case ul {
  display: grid;
  gap: 10px;
  margin: 20px 0 0;
  padding: 0;
  list-style: none;
}

.mobile-vecura__role li,
.mobile-vecura__case li {
  position: relative;
  padding-left: 18px;
  color: rgba(229, 230, 231, 0.78);
  font-size: 0.92rem;
  line-height: 1.52;
}

.mobile-vecura__role li::before,
.mobile-vecura__case li::before {
  position: absolute;
  top: 0.68em;
  left: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-vecura);
  content: "";
}

.mobile-vecura__stats {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin: 24px 0 0;
}

.mobile-vecura__stats article {
  border: 1px solid rgba(229, 230, 231, 0.12);
  border-radius: 18px;
  background: rgba(229, 230, 231, 0.06);
  padding: 18px 20px;
}

.mobile-vecura__stats strong {
  display: block;
  color: var(--color-vecura);
  font-size: 1.72rem;
  line-height: 1;
}

.mobile-vecura__stats span {
  display: block;
  margin-top: 11px;
  color: rgba(229, 230, 231, 0.72);
  font-size: 0.9rem;
  line-height: 1.42;
}

.mobile-vecura__capabilities {
  display: grid;
  gap: 10px;
  margin-top: 22px;
}

.mobile-vecura__capability {
  border: 1px solid rgba(229, 230, 231, 0.12);
  border-radius: 18px;
  background: rgba(229, 230, 231, 0.055);
  overflow: hidden;
}

.mobile-vecura__capability-summary {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 28px;
  gap: 10px;
  align-items: center;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 15px;
  color: inherit;
  cursor: pointer;
  text-align: left;
}

.mobile-vecura__capability-summary::after {
  width: 9px;
  height: 9px;
  margin-top: 5px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  color: #f4f7fa;
  content: "";
  transform: rotate(45deg);
  transition: color 180ms ease, transform 220ms ease;
}

.mobile-vecura__capability--open .mobile-vecura__capability-summary::after {
  color: var(--color-vecura);
  transform: translateY(4px) rotate(225deg);
}

.mobile-vecura__capability-summary svg {
  width: 22px;
  height: 22px;
  color: var(--color-vecura);
}

.mobile-vecura__capabilities strong {
  color: #f4f7fa;
  font-size: 1rem;
}

.mobile-vecura__capabilities p {
  margin: 0;
  border-top: 1px solid rgba(229, 230, 231, 0.1);
  padding: 14px 15px 16px 53px;
  color: rgba(229, 230, 231, 0.72);
  font-size: 0.92rem;
  line-height: 1.52;
}

.mobile-vecura-disclosure-enter-active,
.mobile-vecura-disclosure-leave-active {
  max-height: 280px;
  overflow: hidden;
  transition: max-height 240ms ease, opacity 200ms ease, transform 200ms ease;
}

.mobile-vecura-disclosure-enter-from,
.mobile-vecura-disclosure-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-6px);
}

@media (max-width: 760px) {
  .mobile-vecura {
    display: block;
  }
}
</style>
